import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link, useNavigate } from 'react-router-dom'
import { motion } from 'motion/react'
import {
  Camera,
  Coffee,
  Eye,
  EyeOff,
  ExternalLink,
  Gift,
  Heart,
  ImagePlus,
  Loader2,
  Lock,
  LogOut,
  Pencil,
  PiggyBank,
  Plus,
  Check,
  Copy,
  Palette,
  Ruler,
  Settings,
  Settings2,
  Trash2,
  Unlock,
  X,
  Link2Off,
  ShoppingBag,
} from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'
import { boardSlug } from '@/lib/wishlist'
import { useAuth } from '@/hooks/useAuth'
import { useLanguage } from '@/context/LanguageContext'
import type { TranslationKey } from '@/i18n/homeTranslations'
import {
  orderCategories,
  formatPrice,
  type Wishlist,
  type WishlistCategory,
  type WishlistItem,
} from '@/lib/wishlist'
import { setCustomCategoryColors } from '@/lib/categoryColors'
import CategoryChips from '@/components/wishlist/CategoryChips'
import CategoryManagerDialog from '@/components/wishlist/CategoryManagerDialog'
import DashboardItemImage from '@/components/wishlist/DashboardItemImage'
import { getAppreciationVisitorToken } from '@/components/wishlist/ItemAppreciationButton'
import { parseItemColors } from '@/lib/itemColors'
import WishlistBoardManager from '@/components/wishlist/WishlistBoardManager'
import EmptyState from '@/components/wishlist/EmptyState'
import ItemFormDialog from '@/components/wishlist/ItemFormDialog'
import ItemDetailDialog from '@/components/wishlist/ItemDetailDialog'
import SortableItemsGrid, { SortableItemCard } from '@/components/wishlist/SortableItemsGrid'
import DonationSettingsDialog from '@/components/wishlist/DonationSettingsDialog'
import CarouselManager from '@/components/wishlist/CarouselManager'
import GiftLoader from '@/components/GiftLoader'
import CoverPositionEditor from '@/components/wishlist/CoverPositionEditor'

function PublicLinkMenu({ username, boards, activeBoardId }: { username: string; boards: Wishlist[]; activeBoardId: string | null }) {
  const [copied, setCopied] = useState<'all' | 'board' | null>(null)
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)
  const btnRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const activeBoard = boards.find((b) => b.id === activeBoardId) ?? null

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node
      if (btnRef.current?.contains(target)) return
      if (panelRef.current?.contains(target)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  const toggleOpen = () => {
    const rect = btnRef.current?.getBoundingClientRect()
    if (rect) setPos({ top: rect.bottom + 8, left: Math.max(8, rect.right - 256) })
    setOpen((o) => !o)
  }

  const copy = async (kind: 'all' | 'board') => {
    const url = kind === 'board' && activeBoard
      ? `${window.location.origin}/w/${username}/${boardSlug(activeBoard)}`
      : `${window.location.origin}/w/${username}`
    try {
      await navigator.clipboard.writeText(url)
    } catch {
      // clipboard unavailable: ignore
    }
    setCopied(kind)
    setOpen(false)
    setTimeout(() => setCopied(null), 2000)
  }

  return (
    <div className="relative shrink-0">
      <button
        ref={btnRef}
        onClick={toggleOpen}
        title="Copia link pubblico"
        className={`inline-flex h-7 w-7 items-center justify-center rounded-full transition ${
          copied ? 'bg-green-100 text-green-600' : 'bg-neutral-900 text-white hover:bg-neutral-800'
        }`}
      >
        {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      </button>
      {open && pos && createPortal(
        <div
          ref={panelRef}
          onClick={(e) => e.stopPropagation()}
          style={{ position: 'fixed', top: pos.top, left: pos.left }}
          className="z-[9999] w-64 rounded-2xl border border-border bg-white p-2 shadow-xl"
        >
          <p className="px-2 pb-1.5 pt-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/60">
            Cosa vuoi condividere?
          </p>
          <button
            onClick={() => copy('all')}
            className="w-full rounded-xl px-2.5 py-2 text-left text-sm font-semibold text-neutral-900 transition hover:bg-neutral-100"
          >
            Tutte le bacheche
            <span className="block text-[11px] font-normal text-muted-foreground">
              Link principale con wishlist e consigli per gli acquisti
            </span>
          </button>
          {activeBoard && (
            <button
              onClick={() => copy('board')}
              className="w-full rounded-xl px-2.5 py-2 text-left text-sm font-semibold text-neutral-900 transition hover:bg-neutral-100"
            >
              Solo &ldquo;{activeBoard.title}&rdquo;
              <span className="block text-[11px] font-normal text-muted-foreground">
                Condividi esclusivamente questa bacheca
              </span>
            </button>
          )}
        </div>,
        document.body
      )}
    </div>
  )
}

export default function DashboardPage() {
  const navigate = useNavigate()
  const fabOverDarkRef = useRef(false)
  const fabRef = useRef<HTMLButtonElement>(null)
  const footerRef = useRef<HTMLElement>(null)
  const [fabInverted, setFabInverted] = useState(false)

  const { user, profile, loading, signOut } = useAuth()
  const { t, language } = useLanguage()

  useEffect(() => {
    const btn = fabRef.current
    const footer = footerRef.current
    if (!btn || !footer) return

    let raf = 0
    const isOverDark = () => {
      raf = 0
      const r = btn.getBoundingClientRect()
      const f = footer.getBoundingClientRect()
      const overlaps = r.bottom > f.top && r.top < f.bottom && r.left < f.right && r.right > f.left
      if (overlaps !== fabOverDarkRef.current) {
        fabOverDarkRef.current = overlaps
        setFabInverted(overlaps)
      }
    }
    const schedule = () => { if (!raf) raf = requestAnimationFrame(isOverDark) }
    schedule()
    window.addEventListener('scroll', schedule, { passive: true })
    window.addEventListener('resize', schedule)
    return () => {
      if (raf) cancelAnimationFrame(raf)
      window.removeEventListener('scroll', schedule)
      window.removeEventListener('resize', schedule)
    }
  }, [loading, user])
  const [items, setItems] = useState<WishlistItem[]>([])
  const [fetching, setFetching] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeCategory, setActiveCategory] = useState<string | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<WishlistItem | null>(null)
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [detailItem, setDetailItem] = useState<WishlistItem | null>(null)
  const [detailOpen, setDetailOpen] = useState(false)
  const [customCategories, setCustomCategories] = useState<WishlistCategory[]>([])
  const [categoryManagerOpen, setCategoryManagerOpen] = useState(false)
  const [boards, setBoards] = useState<Wishlist[]>([])
  const [activeBoardId, setActiveBoardId] = useState<string | null>(null)
  const [boardManagerOpen, setBoardManagerOpen] = useState(false)
  const boardScrollRef = useRef<HTMLDivElement>(null)
  const [boardScrollEdges, setBoardScrollEdges] = useState({ left: false, right: false })

  useEffect(() => {
    const el = boardScrollRef.current
    if (!el) return
    const update = () => {
      setBoardScrollEdges({
        left: el.scrollLeft > 4,
        right: el.scrollLeft + el.clientWidth < el.scrollWidth - 4,
      })
    }
    update()
    el.addEventListener('scroll', update, { passive: true })
    const observer = new ResizeObserver(update)
    observer.observe(el)
    return () => {
      el.removeEventListener('scroll', update)
      observer.disconnect()
    }
  }, [boards, loading])
  const [donationOpen, setDonationOpen] = useState(false)
  const [usernameDraft, setUsernameDraft] = useState('')
  const [editingUsername, setEditingUsername] = useState(false)
  const [usernameError, setUsernameError] = useState<string | null>(null)
  const [savingUsername, setSavingUsername] = useState(false)
  const [displayNameDraft, setDisplayNameDraft] = useState('')
  const [editingDisplayName, setEditingDisplayName] = useState(false)
  const [savingDisplayName, setSavingDisplayName] = useState(false)
  const [uploadingCover, setUploadingCover] = useState(false)
  const [coverEditingBoardId, setCoverEditingBoardId] = useState<string | null>(null)

  useEffect(() => {
    setCoverEditingBoardId(null)
  }, [activeBoardId])

  const saveCoverPosition = async (capturedBoardId: string, cover_position: number) => {
    if (!user) throw new Error(language === 'it' ? 'Accedi per salvare la posizione.' : 'Sign in to save the position.')
    const { data, error: saveError } = await supabase
      .from('wishlists')
      .update({ cover_position })
      .eq('id', capturedBoardId)
      .eq('user_id', user.id)
      .select('id')
      .single()
    if (saveError || !data) throw saveError ?? new Error(language === 'it' ? 'Impossibile salvare la posizione.' : 'Unable to save the position.')
    setBoards((current) => current.map((board) => board.id === capturedBoardId ? { ...board, cover_position } : board))
    setCoverEditingBoardId((current) => current === capturedBoardId ? null : current)
  }
  const coverInputRef = useRef<HTMLInputElement>(null)
  const [uploadingAvatar, setUploadingAvatar] = useState(false)
  const avatarInputRef = useRef<HTMLInputElement>(null)
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null)
  const [viewCount, setViewCount] = useState(0)

  useEffect(() => {
    setAvatarUrl(profile?.avatar_url ?? null)
  }, [profile?.avatar_url])

  const uploadAvatar = async (file: File) => {
    if (!user) return
    setUploadingAvatar(true)
    setError(null)
    const ext = file.name.split('.').pop() || 'jpg'
    const path = `${user.id}/avatar-${Date.now()}.${ext}`
    const { error: upErr } = await supabase.storage
      .from('avatars')
      .upload(path, file, { cacheControl: '3600', upsert: true })
    if (upErr) {
      setError(upErr.message)
      setUploadingAvatar(false)
      return
    }
    const { data: urlData } = supabase.storage.from('avatars').getPublicUrl(path)
    const finalUrl = urlData.publicUrl
    const { error: dbErr } = await supabase
      .from('profiles')
      .update({ avatar_url: finalUrl })
      .eq('id', user.id)
    setUploadingAvatar(false)
    if (dbErr) {
      setError(dbErr.message)
      return
    }
    setAvatarUrl(finalUrl)
  }

  const activeBoard = useMemo(
    () => boards.find((b) => b.id === activeBoardId) ?? null,
    [boards, activeBoardId]
  )

  const uploadCover = async (file: File) => {
    if (!user || !activeBoardId) return
    setUploadingCover(true)
    const ext = file.name.split('.').pop() || 'jpg'
    const path = `${user.id}/${activeBoardId}-${Date.now()}.${ext}`
    const { error: upErr } = await supabase.storage
      .from('wishlist-covers')
      .upload(path, file, { cacheControl: '3600', upsert: true })
    if (upErr) {
      setError(upErr.message)
      setUploadingCover(false)
      return
    }
    const { data: urlData } = supabase.storage.from('wishlist-covers').getPublicUrl(path)
    const coverUrl = urlData.publicUrl
    const { error: dbErr } = await supabase
      .from('wishlists')
      .update({ cover_url: coverUrl, cover_position: 50 })
      .eq('id', activeBoardId)
    setUploadingCover(false)
    if (dbErr) {
      setError(dbErr.message)
      return
    }
    setBoards((prev) =>
      prev.map((b) => (b.id === activeBoardId ? { ...b, cover_url: coverUrl, cover_position: 50 } : b))
    )
    notifyFollowers(activeBoardId)
  }

  const removeCover = async () => {
    if (!activeBoardId) return
    setUploadingCover(true)
    const { error: dbErr } = await supabase
      .from('wishlists')
      .update({ cover_url: null })
      .eq('id', activeBoardId)
    setUploadingCover(false)
    if (dbErr) {
      setError(dbErr.message)
      return
    }
    setBoards((prev) =>
      prev.map((b) => (b.id === activeBoardId ? { ...b, cover_url: null } : b))
    )
    notifyFollowers(activeBoardId)
  }

  useEffect(() => {
    if (!loading && !user) navigate('/login')
  }, [loading, user, navigate])

  useEffect(() => {
    if (!user) return
    const load = async () => {
      setFetching(true)
      const { data, error: err } = await supabase
        .from('wishlist_items')
        .select('*')
        .eq('user_id', user.id)
        .order('position', { ascending: true })
        .order('created_at', { ascending: false })
      if (err) setError(err.message)
      else setItems((data ?? []) as unknown as WishlistItem[])
      setFetching(false)
    }
    load()
  }, [user?.id])

  useEffect(() => {
    if (!user) return
    const loadBoards = async () => {
      const { data } = await supabase
        .from('wishlists')
        .select('*')
        .eq('user_id', user.id)
        .order('position', { ascending: true })
      let list = (data ?? []) as unknown as Wishlist[]
      if (list.length === 0) {
        const { data: created } = await supabase
          .from('wishlists')
          .insert({ user_id: user.id, title: t('dash.defaultWishlistTitle'), position: 0 })
          .select()
          .single()
        if (created) list = [created as unknown as Wishlist]
      }
      setBoards(list)
      setActiveBoardId((prev) => prev ?? list[0]?.id ?? null)
    }
    loadBoards()
  }, [user?.id])

  useEffect(() => {
    if (!user) return
    supabase
      .from('wishlist_categories')
      .select('*')
      .eq('user_id', user.id)
      .order('position', { ascending: true })
      .then(({ data }) => setCustomCategories((data ?? []) as unknown as WishlistCategory[]))
  }, [user?.id])

  useEffect(() => {
    setCustomCategoryColors(user?.id, customCategories)
  }, [user?.id, customCategories])

  useEffect(() => {
    if (!user || !activeBoardId) return
    supabase
      .rpc('get_wishlist_views', { p_wishlist_id: activeBoardId })
      .then(({ data }) => setViewCount((data as number) ?? 0))
  }, [user?.id, activeBoardId])

  const boardItems = useMemo(() => {
    if (!activeBoardId || boards.length === 0) return items
    const firstBoardId = boards[0]?.id
    return items.filter((i) =>
      i.wishlist_id ? i.wishlist_id === activeBoardId : activeBoardId === firstBoardId
    )
  }, [items, activeBoardId, boards])

  const categories = useMemo(
    () =>
      orderCategories(
        Array.from(new Set(boardItems.map((i) => i.category))),
        customCategories
      ),
    [boardItems, customCategories]
  )
  const counts = useMemo(() => {
    const map: Record<string, number> = {}
    for (const i of boardItems) map[i.category] = (map[i.category] ?? 0) + 1
    return map
  }, [boardItems])
  const filtered = useMemo(
    () =>
      activeCategory
        ? boardItems.filter((i) => i.category === activeCategory)
        : boardItems,
    [boardItems, activeCategory]
  )
  const reservedCount = useMemo(
    () => boardItems.filter((i) => i.is_reserved).length,
    [boardItems]
  )

  const isGuideBoard = activeBoard?.kind === 'guide'
  const boardItemIds = useMemo(() => boardItems.map((i) => i.id), [boardItems])
  const [appreciationTotals, setAppreciationTotals] = useState<Record<string, number>>({})
  const appreciationCount = useMemo(
    () => boardItemIds.reduce((sum, id) => sum + (appreciationTotals[id] ?? 0), 0),
    [boardItemIds, appreciationTotals]
  )

  useEffect(() => {
    if (!isGuideBoard || boardItemIds.length === 0) {
      setAppreciationTotals({})
      return
    }
    let cancelled = false
    const load = async () => {
      try {
        const token = getAppreciationVisitorToken()
        const next: Record<string, number> = {}
        for (let offset = 0; offset < boardItemIds.length; offset += 500) {
          const { data, error } = await supabase.rpc('get_guide_item_appreciations', {
            _item_ids: boardItemIds.slice(offset, offset + 500),
            _visitor_token: token,
          })
          if (cancelled) return
          if (error) throw error
          const rows = data as unknown as { item_id: string; appreciation_count: number }[] | null
          for (const row of rows ?? []) {
            next[row.item_id] = row.appreciation_count
          }
        }
        if (!cancelled) setAppreciationTotals(next)
      } catch (cause) {
        console.error('Unable to load guide appreciations', cause)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [isGuideBoard, boardItemIds])

  const openNew = () => {
    setEditing(null)
    setDialogOpen(true)
  }
  const openEdit = (item: WishlistItem) => {
    setEditing(item)
    setDialogOpen(true)
  }
  const onSaved = (saved: WishlistItem) => {
    setItems((prev) =>
      prev.some((i) => i.id === saved.id)
        ? prev.map((i) => (i.id === saved.id ? saved : i))
        : [saved, ...prev]
    )
    setDialogOpen(false)
    setEditing(null)
  }
  const notifyFollowers = (wishlistId: string | null | undefined) => {
    if (!wishlistId) return
    supabase.functions
      .invoke('notify-board-followers', { body: { wishlist_id: wishlistId } })
      .catch((e) => console.error('notify-board-followers failed', e))
  }

  const remove = async (id: string) => {
    setDeletingId(id)
    notifyFollowers(items.find((i) => i.id === id)?.wishlist_id ?? activeBoardId)
    const { error: err } = await supabase.from('wishlist_items').delete().eq('id', id)
    if (!err) setItems((prev) => prev.filter((i) => i.id !== id))
    setDeletingId(null)
    setConfirmDeleteId(null)
  }
  const [unblockingId, setUnblockingId] = useState<string | null>(null)
  const unblockItem = async (id: string) => {
    setUnblockingId(id)
    const { error: err } = await supabase
      .from('wishlist_items')
      .update({ is_reserved: false, reserved_by: null, reserved_session_id: null })
      .eq('id', id)
    if (!err) {
      setItems((prev) =>
        prev.map((i) =>
          i.id === id
            ? { ...i, is_reserved: false, reserved_by: null, reserved_at: null }
            : i
        )
      )
      setDetailItem((prev) =>
        prev && prev.id === id
          ? { ...prev, is_reserved: false, reserved_by: null, reserved_at: null }
          : prev
      )
    }
    setUnblockingId(null)
  }
  const [togglingHideId, setTogglingHideId] = useState<string | null>(null)
  const toggleHidden = async (id: string, current: boolean) => {
    setTogglingHideId(id)
    const next = !current
    const { error: err } = await supabase
      .from('wishlist_items')
      .update({ is_hidden: next })
      .eq('id', id)
    if (!err) {
      setItems((prev) =>
        prev.map((i) => (i.id === id ? { ...i, is_hidden: next } : i))
      )
      setDetailItem((prev) =>
        prev && prev.id === id ? { ...prev, is_hidden: next } : prev
      )
    }
    setTogglingHideId(null)
  }
  const handleSignOut = async () => {
    await signOut()
    navigate('/')
  }

  const saveUsername = async () => {
    const next = usernameDraft.trim().replace(/[^a-zA-Z0-9_.-]/g, '')
    if (!next || savingUsername) return
    setSavingUsername(true)
    setUsernameError(null)
    const { data: taken } = await supabase
      .from('profiles')
      .select('id')
      .ilike('username', next)
      .neq('id', user.id)
      .maybeSingle()
    if (taken) {
      setSavingUsername(false)
      setUsernameError(`@${next} è già stato preso da un altro utente. Prova con un altro nome!`)
      return
    }
    const { error: err } = await supabase
      .from('profiles')
      .update({ username: next, username_changed_at: new Date().toISOString() })
      .eq('id', user.id)
    setSavingUsername(false)
    if (err) {
      setUsernameError(
        err.message.includes('una sola volta')
          ? 'Hai già modificato lo username in passato: non puoi più cambiarlo.'
          : err.message.includes('unique') || err.message.includes('duplicate')
            ? 'Questo username è già in uso, provane un altro.'
            : err.message
      )
      return
    }
    setEditingUsername(false)
    window.location.reload()
  }

  const saveDisplayName = async () => {
    const next = displayNameDraft.trim()
    if (!next || savingDisplayName) return
    setSavingDisplayName(true)
    const { error: err } = await supabase
      .from('profiles')
      .update({ display_name: next })
      .eq('id', user.id)
    setSavingDisplayName(false)
    if (err) {
      setError(err.message)
      return
    }
    setEditingDisplayName(false)
    window.location.reload()
  }

  const onBoardsChange = (next: Wishlist[]) => {
    setBoards(next)
    if (activeBoardId && !next.some((b) => b.id === activeBoardId)) {
      setActiveBoardId(next[0]?.id ?? null)
      setActiveCategory(null)
    }
  }

  const togglePrivate = async () => {
    if (!activeBoardId) return
    const next = !activeBoard?.is_private
    setBoards((prev) =>
      prev.map((b) => (b.id === activeBoardId ? { ...b, is_private: next } : b))
    )
    const { error } = await supabase.from('wishlists').update({ is_private: next }).eq('id', activeBoardId)
    if (!error) notifyFollowers(activeBoardId)
    if (error) {
      // Revert optimistic update on error
      setBoards((prev) =>
        prev.map((b) => (b.id === activeBoardId ? { ...b, is_private: !next } : b))
      )
      console.error('Failed to toggle privacy:', error)
    }
  }

  const handleReorder = (orderedIds: string[]) => {
    const byId = new Map(items.map((i) => [i.id, i]))
    const reorderedVisible = orderedIds
      .map((id) => byId.get(id))
      .filter((i): i is WishlistItem => !!i)
    const rest = items.filter((i) => !orderedIds.includes(i.id))
    const next = activeCategory ? [...reorderedVisible, ...rest] : reorderedVisible
    setItems(next)
    void Promise.all(
      reorderedVisible.map((item, idx) =>
        supabase.from('wishlist_items').update({ position: idx }).eq('id', item.id)
      )
    )
  }

  if (loading || !user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <GiftLoader />
      </div>
    )
  }

  const addButton = (
    <button
      onClick={openNew}
      className="inline-flex items-center justify-center text-white bg-neutral-900 rounded-full w-14 h-14 hover:bg-neutral-800 transition-colors shadow-lg"
      title="Aggiungi desiderio"
    >
      <Plus className="h-6 w-6" />
    </button>
  )

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-20 glass-strong border-b border-border/30">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-2 px-4 py-2.5 sm:gap-3 sm:py-3">
          <Link to="/" className="flex items-center gap-2 font-display text-base font-semibold text-foreground sm:text-lg">
            <span className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-neutral-900 text-white flex items-center justify-center">
              <Gift className="w-4 h-4 sm:w-5 sm:h-5" />
            </span>
            <span className="hidden sm:inline font-logo">Make me a Gift</span>
          </Link>
          <div className="flex items-center gap-1.5 sm:gap-2">
            <a
              href={`/w/${profile?.username || user.id}`}
              target="_blank"
              rel="noreferrer"
              title="Anteprima pubblica"
              className="flex h-9 w-9 sm:h-10 sm:w-10 items-center justify-center rounded-full border border-border glass text-muted-foreground transition hover:border-sky-300 hover:text-sky-500 hover:bg-sky-50"
            >
              <Eye className="h-4 w-4" />
            </a>
            <Link
              to="/profile"
              title="Profilo"
              className="flex h-9 w-9 sm:h-10 sm:w-10 items-center justify-center rounded-full border border-border glass text-muted-foreground transition hover:border-violet-300 hover:text-violet-500 hover:bg-violet-50"
            >
              <Settings className="h-4 w-4" />
            </Link>
            <button
              onClick={togglePrivate}
              title={activeBoard?.is_private ? 'Wishlist privata — clicca per renderla pubblica' : 'Wishlist pubblica — clicca per renderla privata'}
              className={`flex h-9 w-9 sm:h-10 sm:w-10 items-center justify-center rounded-full border transition ${
                activeBoard?.is_private
                  ? 'border-amber-300 bg-amber-50 text-amber-600 hover:bg-amber-100'
                  : 'border-[#6bc496] bg-[#C8E6D7] text-[#4A9B6F] hover:bg-[#B8DCC7] hover:text-[#3D8A5F]'
              }`}
            >
              {activeBoard?.is_private ? <Lock className="h-4 w-4" /> : <Unlock className="h-4 w-4" />}
            </button>
            <button
              onClick={() => setDonationOpen(true)}
              title="Salvadanaio"
              className="flex h-9 w-9 sm:h-10 sm:w-10 items-center justify-center rounded-full border border-border glass text-muted-foreground transition hover:border-pink-300 hover:text-pink-500 hover:bg-pink-50"
            >
              <PiggyBank className="h-4 w-4" />
            </button>
            <button
              onClick={handleSignOut}
              title="Esci"
              className="flex h-9 w-9 sm:h-10 sm:w-10 items-center justify-center rounded-full border border-border glass text-muted-foreground transition hover:border-red-300 hover:text-red-500 hover:bg-red-50"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-6 sm:py-10">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between"
        >
          <div className="flex items-start gap-3 sm:gap-4">
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (file) uploadAvatar(file)
                e.target.value = ''
              }}
            />
            <button
              onClick={() => avatarInputRef.current?.click()}
              disabled={uploadingAvatar}
              className="relative group/avatar shrink-0"
              title="Cambia avatar"
            >
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt="Avatar"
                  className="w-14 h-14 sm:w-20 sm:h-20 rounded-full object-cover ring-2 ring-border"
                />
              ) : (
                <div className="w-14 h-14 sm:w-20 sm:h-20 rounded-full bg-neutral-900 text-white font-extrabold text-xl sm:text-2xl flex items-center justify-center ring-2 ring-border">
                  {(profile?.display_name?.trim() || profile?.username || 'U').slice(0, 1).toUpperCase()}
                </div>
              )}
              <span className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover/avatar:opacity-100 transition-opacity flex items-center justify-center">
                {uploadingAvatar ? (
                  <Loader2 className="w-5 h-5 text-white animate-spin" />
                ) : (
                  <Camera className="w-5 h-5 text-white" />
                )}
              </span>
            </button>
            <div>
            <h1 className="flex items-center gap-2 text-2xl font-extrabold sm:text-3xl md:text-4xl">
              {t('dash.hello')}, {profile?.display_name?.trim() || profile?.username || 'tu'} 👋🏻
              {!editingDisplayName && (
                <button
                  onClick={() => {
                    setDisplayNameDraft(profile?.display_name ?? '')
                    setEditingDisplayName(true)
                  }}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition hover:bg-neutral-100 hover:text-neutral-900"
                  title="Modifica il tuo nome pubblico"
                >
                  <Pencil className="h-4 w-4" />
                </button>
              )}
            </h1>
            {editingDisplayName && (
              <div className="mt-2 flex items-center gap-2">
                <input
                  autoFocus
                  value={displayNameDraft}
                  onChange={(e) => setDisplayNameDraft(e.target.value)}
                  onKeyDown={async (e) => {
                    if (e.key === 'Enter') {
                      setSavingDisplayName(true)
                      const { error } = await supabase
                        .from('profiles')
                        .update({ display_name: displayNameDraft.trim() })
                        .eq('id', user.id)
                      if (!error) setEditingDisplayName(false)
                      setSavingDisplayName(false)
                    } else if (e.key === 'Escape') {
                      setEditingDisplayName(false)
                    }
                  }}
                  className="w-full max-w-xs rounded-lg border border-neutral-300 bg-white px-3 py-1.5 text-sm font-semibold text-neutral-900 outline-none focus:border-neutral-900"
                  placeholder="Il tuo nome pubblico"
                />
                <button
                  onClick={async () => {
                    setSavingDisplayName(true)
                    const { error } = await supabase
                      .from('profiles')
                      .update({ display_name: displayNameDraft.trim() })
                      .eq('id', user.id)
                    if (!error) setEditingDisplayName(false)
                    setSavingDisplayName(false)
                  }}
                  disabled={savingDisplayName}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-neutral-900 text-white transition hover:bg-neutral-800 disabled:opacity-50"
                >
                  {savingDisplayName ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                </button>
                <button
                  onClick={() => setEditingDisplayName(false)}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition hover:bg-neutral-100 hover:text-neutral-900"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
            <p className="mt-1.5 sm:mt-2 max-w-xl text-sm sm:text-base text-muted-foreground">
              {t('dash.subtitle')}
            </p>
            </div>
          </div>

          {/* Mobile: full-width public link row */}
          <div className="sm:hidden">
            {profile?.username && !editingUsername && (
              <>
                {profile.username_changed_at ? (
                  <div className="group flex w-full items-center gap-2 rounded-full border border-border bg-white/70 backdrop-blur-sm pl-3.5 pr-1.5 py-1.5 transition hover:border-neutral-300 hover:shadow-sm">
                    <span className="text-[11px] font-medium text-muted-foreground/60 shrink-0">Link:</span>
                    <span className="text-xs font-semibold text-foreground truncate flex-1 sm:flex-none sm:max-w-none">
                      makemeagift.shop/w/{profile.username}
                    </span>
                    <PublicLinkMenu username={profile.username} boards={boards} activeBoardId={activeBoardId} />
                  </div>
                ) : (
                  <button
                    onClick={() => {
                      setUsernameDraft(profile.username ?? '')
                      setUsernameError(null)
                      setEditingUsername(true)
                    }}
                    className="inline-flex items-center gap-1 rounded-full border border-border bg-white px-3 py-1.5 text-xs font-semibold text-muted-foreground transition hover:border-neutral-300 hover:text-neutral-900"
                    title="Puoi modificarlo una sola volta"
                  >
                    <Pencil className="h-3 w-3" />
                    Modifica username (una sola volta)
                  </button>
                )}
              </>
            )}
            {!profile?.username && !editingUsername && (
              <button
                onClick={() => {
                  setUsernameDraft('')
                  setUsernameError(null)
                  setEditingUsername(true)
                }}
                className="inline-flex items-center gap-1 rounded-full border border-border bg-white px-3 py-1 text-xs font-semibold text-muted-foreground transition hover:border-neutral-300 hover:text-neutral-900"
              >
                <Pencil className="h-3 w-3" />
                Scegli il tuo @username pubblico
              </button>
            )}
            {editingUsername && (
              <div className="max-w-sm">
                <label className="block text-xs font-semibold text-muted-foreground/70 mb-1.5">
                  Link pubblico
                </label>
                <p className="text-xs text-muted-foreground/50 mb-1.5 break-all">
                  makemeagift.shop/w/
                </p>
                <div className="flex items-center gap-2">
                  <input
                    autoFocus
                    value={usernameDraft}
                    onChange={(e) =>
                      setUsernameDraft(e.target.value.replace(/[^a-zA-Z0-9_.-]/g, ''))
                    }
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveUsername()
                      if (e.key === 'Escape') setEditingUsername(false)
                    }}
                    placeholder="es. Aura"
                    className="min-w-0 flex-1 rounded-xl border border-border bg-white px-3 py-2 text-sm font-semibold text-foreground outline-none focus:border-neutral-300 focus:ring-2 focus:ring-neutral-900/10"
                  />
                  <button
                    onClick={saveUsername}
                    disabled={savingUsername || usernameDraft.trim().length < 3}
                    className="inline-flex shrink-0 items-center gap-1 rounded-full bg-neutral-900 px-4 py-2 text-xs font-semibold text-white transition hover:bg-neutral-800 disabled:opacity-50"
                  >
                    {savingUsername && <Loader2 className="h-3 w-3 animate-spin" />}
                    Salva
                  </button>
                  <button
                    onClick={() => setEditingUsername(false)}
                    className="shrink-0 rounded-full border border-border px-4 py-2 text-xs font-semibold text-muted-foreground hover:bg-muted/50"
                  >
                    Annulla
                  </button>
                </div>
                <p className="mt-2 text-[11px] text-muted-foreground/60">
                  Lo username si può modificare una sola volta: sceglilo con cura!
                </p>
                {usernameError && <p className="mt-1 text-xs text-red-600">{usernameError}</p>}
              </div>
            )}
          </div>

          {/* Desktop/tablet: inline public link (original placement) */}
          <div className="hidden sm:block">
            {profile?.username && !editingUsername && (
              <div className="mt-3 flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-2">
                {profile.username_changed_at ? (
                  <div className="group flex w-full sm:w-auto items-center gap-2 rounded-full border border-border bg-white/70 backdrop-blur-sm pl-3.5 pr-1.5 py-1.5 transition hover:border-neutral-300 hover:shadow-sm">
                    <span className="text-[11px] font-medium text-muted-foreground/60 shrink-0">Link:</span>
                    <span className="text-xs font-semibold text-foreground truncate flex-1 sm:flex-none sm:max-w-none">
                      makemeagift.shop/w/{profile.username}
                    </span>
                    <PublicLinkMenu username={profile.username} boards={boards} activeBoardId={activeBoardId} />
                  </div>
                ) : (
                  <button
                    onClick={() => {
                      setUsernameDraft(profile.username ?? '')
                      setUsernameError(null)
                      setEditingUsername(true)
                    }}
                    className="inline-flex items-center gap-1 rounded-full border border-border bg-white px-3 py-1.5 text-xs font-semibold text-muted-foreground transition hover:border-neutral-300 hover:text-neutral-900"
                    title="Puoi modificarlo una sola volta"
                  >
                    <Pencil className="h-3 w-3" />
                    Modifica username (una sola volta)
                  </button>
                )}
              </div>
            )}
            {!profile?.username && !editingUsername && (
              <button
                onClick={() => {
                  setUsernameDraft('')
                  setUsernameError(null)
                  setEditingUsername(true)
                }}
                className="mt-2 inline-flex items-center gap-1 rounded-full border border-border bg-white px-3 py-1 text-xs font-semibold text-muted-foreground transition hover:border-neutral-300 hover:text-neutral-900"
              >
                <Pencil className="h-3 w-3" />
                Scegli il tuo @username pubblico
              </button>
            )}
            {editingUsername && (
              <div className="mt-3 max-w-sm">
                <label className="block text-xs font-semibold text-muted-foreground/70 mb-1.5">
                  Link pubblico
                </label>
                <p className="text-xs text-muted-foreground/50 mb-1.5 break-all">
                  makemeagift.shop/w/
                </p>
                <div className="flex items-center gap-2">
                  <input
                    autoFocus
                    value={usernameDraft}
                    onChange={(e) =>
                      setUsernameDraft(e.target.value.replace(/[^a-zA-Z0-9_.-]/g, ''))
                    }
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveUsername()
                      if (e.key === 'Escape') setEditingUsername(false)
                    }}
                    placeholder="es. Aura"
                    className="min-w-0 flex-1 rounded-xl border border-border bg-white px-3 py-2 text-sm font-semibold text-foreground outline-none focus:border-neutral-300 focus:ring-2 focus:ring-neutral-900/10"
                  />
                  <button
                    onClick={saveUsername}
                    disabled={savingUsername || usernameDraft.trim().length < 3}
                    className="inline-flex shrink-0 items-center gap-1 rounded-full bg-neutral-900 px-4 py-2 text-xs font-semibold text-white transition hover:bg-neutral-800 disabled:opacity-50"
                  >
                    {savingUsername && <Loader2 className="h-3 w-3 animate-spin" />}
                    Salva
                  </button>
                  <button
                    onClick={() => setEditingUsername(false)}
                    className="shrink-0 rounded-full border border-border px-4 py-2 text-xs font-semibold text-muted-foreground hover:bg-muted/50"
                  >
                    Annulla
                  </button>
                </div>
                <p className="mt-2 text-[11px] text-muted-foreground/60">
                  Lo username si può modificare una sola volta: sceglilo con cura!
                </p>
                {usernameError && <p className="mt-1 text-xs text-red-600">{usernameError}</p>}
              </div>
            )}
          </div>
          <div className="flex w-full min-w-0 flex-col gap-3 sm:w-auto sm:items-end">
            {boards.length > 0 && (
              <div className="flex min-w-0 items-center gap-2 sm:flex-wrap sm:justify-end">
                <div className="relative min-w-0 flex-1 sm:contents">
                  <div
                    ref={boardScrollRef}
                    className="flex items-center gap-2 overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden sm:contents"
                  >
                    {boards.map((board) => (
                      <button
                        key={board.id}
                        aria-pressed={activeBoardId === board.id}
                        onClick={() => {
                          setActiveBoardId(board.id)
                          setActiveCategory(null)
                        }}
                        className={`inline-flex h-10 shrink-0 items-center whitespace-nowrap rounded-full px-4 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-900 focus-visible:ring-inset sm:h-auto sm:py-2 ${
                          activeBoardId === board.id
                            ? 'bg-neutral-900 text-white'
                            : 'glass text-neutral-500 hover:text-neutral-900 sm:border sm:border-border sm:bg-white sm:hover:border-neutral-300'
                        }`}
                      >
                        {board.title}
                      </button>
                    ))}
                  </div>
                  <div
                    aria-hidden="true"
                    className={`pointer-events-none absolute inset-y-0 left-0 w-12 bg-gradient-to-r from-background via-background/60 to-transparent transition-opacity duration-200 sm:hidden ${boardScrollEdges.left ? 'opacity-100' : 'opacity-0'}`}
                  />
                  <div
                    aria-hidden="true"
                    className={`pointer-events-none absolute inset-y-0 right-0 w-12 bg-gradient-to-l from-background via-background/60 to-transparent transition-opacity duration-200 sm:hidden ${boardScrollEdges.right ? 'opacity-100' : 'opacity-0'}`}
                  />
                </div>
                <button
                  onClick={() => setBoardManagerOpen(true)}
                  title="Gestisci bacheche"
                  aria-label="Gestisci bacheche"
                  className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-neutral-900 text-white transition hover:bg-neutral-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-900 focus-visible:ring-offset-2"
                >
                  <Settings2 className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>

          {/* Cover banner for active board */}
          {activeBoardId && (
          <div className="order-2 w-full mt-2 sm:order-last sm:mt-2">
            <input
              ref={coverInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (file) uploadCover(file)
                e.target.value = ''
              }}
            />
            <div className="relative min-h-[200px] sm:min-h-[260px] rounded-xl overflow-hidden border border-border bg-neutral-100 group">
              <div className="pointer-events-none absolute bottom-2 left-3 z-20 rounded-full bg-black/60 px-3 py-1 text-[11px] font-medium text-white opacity-0 backdrop-blur transition-opacity group-hover:opacity-100">
                {t('dash.coverSizeHint')}
              </div>
              {activeBoard?.cover_url ? (
                <>
                  <img
                    src={activeBoard.cover_url} style={{ objectPosition: `50% ${activeBoard.cover_position ?? 50}%` }}
                    alt="Cover"
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                  <div className="absolute bottom-3 left-3 right-3 flex flex-nowrap items-end justify-center gap-1.5 sm:gap-2 sm:justify-end opacity-100 sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100 transition-opacity">
                    <button
                      type="button"
                      onClick={() => setCoverEditingBoardId(activeBoard.id)}
                      disabled={uploadingCover}
                      className="inline-flex shrink-0 items-center gap-1 rounded-full bg-white/90 px-2.5 py-2 text-xs font-semibold text-neutral-900 backdrop-blur transition hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-900 focus-visible:ring-offset-2 disabled:opacity-50 sm:gap-1.5 sm:px-4"
                    >
                      <Settings2 className="h-3.5 w-3.5" />
                      {language === 'it' ? 'Riposiziona' : 'Reposition'}
                    </button>
                    <button
                      onClick={() => coverInputRef.current?.click()}
                      disabled={uploadingCover}
                      className="inline-flex shrink-0 items-center gap-1 rounded-full bg-white/90 px-2.5 py-2 text-xs font-semibold text-neutral-900 backdrop-blur transition hover:bg-white disabled:opacity-50 sm:gap-1.5 sm:px-4"
                    >
                      {uploadingCover ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <ImagePlus className="h-3.5 w-3.5" />
                      )}
                      {t('dash.changeCover')}
                    </button>
                    <button
                      onClick={removeCover}
                      disabled={uploadingCover}
                      className="inline-flex shrink-0 items-center gap-1 rounded-full bg-white/90 px-3 py-2 text-xs font-semibold text-red-600 backdrop-blur transition hover:bg-white disabled:opacity-50 sm:gap-1.5 sm:px-4"
                    >
                      <X className="h-3.5 w-3.5" />
                      {t('dash.remove')}
                    </button>
                  </div>
                </>
              ) : (
                <button
                  onClick={() => coverInputRef.current?.click()}
                  disabled={uploadingCover}
                  className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-muted-foreground transition hover:text-neutral-900 disabled:opacity-50"
                >
                  {uploadingCover ? (
                    <Loader2 className="h-6 w-6 animate-spin" />
                  ) : (
                    <ImagePlus className="h-6 w-6" />
                  )}
                  <span className="text-sm font-medium">
                    {uploadingCover ? t('dash.uploading') : t('dash.addCover')}
                  </span>
                </button>
              )}
              {activeBoard?.cover_url && coverEditingBoardId === activeBoard.id && (
                <CoverPositionEditor
                  key={activeBoard.id}
                  imageUrl={activeBoard.cover_url}
                  position={activeBoard.cover_position ?? 50}
                  language={language}
                  onSave={(position) => saveCoverPosition(activeBoard.id, position)}
                  onCancel={() => setCoverEditingBoardId(null)}
                />
              )}
            </div>
          </div>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="mt-6 grid grid-cols-3 gap-2 sm:gap-4"
        >
          {[
            { icon: Gift, label: t('dash.totalWishes'), value: boardItems.length, color: 'text-emerald-600 bg-emerald-100' },
            { icon: Eye, label: t('dash.views'), value: viewCount, color: 'text-sky-500 bg-sky-100' },
            isGuideBoard
              ? { icon: Heart, label: t('dash.likes'), value: appreciationCount, color: 'text-fuchsia-600 bg-fuchsia-100' }
              : { icon: Heart, label: t('dash.reserved'), value: reservedCount, color: 'text-fuchsia-600 bg-fuchsia-100' },
          ].map(({ icon: Icon, label, value, color }) => (
            <div key={label} className="glass rounded-2xl p-3 sm:p-4 hover-glow flex flex-col items-center text-center sm:flex-row sm:items-center sm:gap-3 sm:text-left">
              <div className={`inline-flex rounded-full p-2 sm:p-2.5 ${color}`}>
                <Icon className="h-5 w-5" />
              </div>
              <div className="mt-2 sm:mt-0">
                <p className="text-xl sm:text-2xl font-extrabold leading-tight">{value}</p>
                <p className="text-[10px] sm:text-xs font-medium text-muted-foreground">{label}</p>
              </div>
            </div>
          ))}
        </motion.div>

        {categories.length > 0 && (
          <div className="mt-8 flex items-center gap-2">
            <div className="min-w-0 flex-1">
              <CategoryChips
                categories={categories}
                active={activeCategory}
                onChange={setActiveCategory}
                counts={counts}
              />
            </div>
            <button
              onClick={() => setCategoryManagerOpen(true)}
              title="Gestisci categorie"
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-neutral-900 text-white transition hover:bg-neutral-800"
            >
              <Settings2 className="h-4 w-4" />
            </button>
          </div>
        )}

        {categories.length === 0 && !fetching && (
          <button
            onClick={() => setCategoryManagerOpen(true)}
            className="mt-8 inline-flex items-center gap-1.5 rounded-full border border-border bg-white px-4 py-2 text-sm font-semibold text-muted-foreground transition hover:border-neutral-300 /40 hover:text-primary"
          >
            <Settings2 className="h-4 w-4" />
            Crea le tue categorie
          </button>
        )}

        {error && <p className="mt-6 text-sm text-red-600">{error}</p>}

        {fetching ? (
          <div className="mt-8 grid grid-cols-2 gap-3 sm:gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {[0, 1, 2, 3].map((n) => (
              <div key={n} className={`overflow-hidden glass rounded-lg hover-glow`}>
                <div className="aspect-[4/3] animate-pulse bg-muted" />
                <div className="space-y-3 p-5">
                  <div className="h-3 w-16 animate-pulse rounded-full bg-muted" />
                  <div className="h-4 w-3/4 animate-pulse rounded-full bg-muted" />
                  <div className="h-3 w-1/3 animate-pulse rounded-full bg-muted" />
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 && items.length === 0 ? (
          <EmptyState action={addButton} />
        ) : filtered.length === 0 ? (
          <p className="mt-10 text-center text-muted-foreground">
            Nessun desiderio in questa categoria.
          </p>
        ) : (
          <SortableItemsGrid
            ids={filtered.map((i) => i.id)}
            onReorder={handleReorder}
          >
            <motion.div
              initial="hidden"
              animate="visible"
              variants={{
                hidden: { opacity: 0 },
                visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
              }}
              className="mt-6 grid grid-cols-2 gap-3 sm:gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
            >
              {filtered.map((item) => (
                <SortableItemCard key={item.id} id={item.id}>
                  <motion.article
                    variants={{
                      hidden: { opacity: 0, y: 20 },
                      visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
                    }}
                    whileHover={{ y: -4 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                    onClick={() => { setDetailItem(item); setDetailOpen(true) }}
                    className={`h-full overflow-hidden rounded-lg glass hover-glow cursor-pointer transition-opacity ${item.is_hidden ? 'opacity-40' : ''}`}
                  >
                <DashboardItemImage
                  imageUrl={item.image_url}
                  title={item.title}
                  isReserved={item.is_reserved}
                  isHidden={item.is_hidden}
                  category={item.category}
                  bgColor={item.bg_color}
                  isOwner
                />
                <div className="p-3 md:p-4">
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-foreground line-clamp-1 mb-0.5 text-sm md:text-base">
                        {item.title}
                      </h3>

                      {(item as WishlistItem & { is_recurring?: boolean }).is_recurring && (
                        <p className="text-xs md:text-sm font-semibold line-clamp-1 mb-1" style={{ color: '#23C55E' }}>
                          <span className="md:hidden">Acquisto ricorrente</span>
                          <span className="hidden md:inline">{language === 'en' ? 'Recurring purchases allowed' : 'Consentito acquisto ricorrente'}</span>
                        </p>
                      )}
                    </div>
                    {(() => {
                      const spheres = parseItemColors(item.color, `${item.title} ${item.description ?? ''}`)
                      return spheres.length > 0 ? (
                        <div className="flex items-center shrink-0">
                          {spheres.map((c, i) => (
                            <span
                              key={i}
                              className="w-5 h-5 md:w-6 md:h-6 rounded-full shadow-sm"
                              style={{
                                backgroundColor: c,
                                border: '3px solid white',
                                marginLeft: i === 0 ? 0 : '-8px',
                              }}
                              title={item.color ?? ''}
                            />
                          ))}
                        </div>
                      ) : null
                    })()}
                  </div>
                  {activeBoard?.kind === 'guide' && item.description && (
                    <p className="mt-1 text-[11px] md:text-xs text-muted-foreground line-clamp-3">
                      {item.description}
                    </p>
                  )}
                  <div className="flex flex-col gap-1 mt-1 md:flex-row md:items-center md:justify-between md:gap-2">
                    <div className="flex items-center gap-2 min-w-0 flex-wrap">
                      {item.size && (
                        <p className="text-[10px] md:text-xs text-muted-foreground inline-flex items-center gap-1 shrink-0">
                          <Ruler className="w-3 h-3" />
                          {item.size}
                        </p>
                      )}
                      {item.color_text && (
                        <p className="text-[10px] md:text-xs text-muted-foreground inline-flex items-center gap-1 shrink-0 truncate">
                          <Palette className="w-3 h-3" />
                          {item.color_text}
                        </p>
                      )}
                    </div>
                    {item.price != null && (
                      <span className="text-base md:text-sm font-bold md:font-semibold gradient-text-price justify-center md:justify-start flex">
                        {formatPrice(item.price)}
                      </span>
                    )}
                  </div>
                  {item.is_reserved && item.reserved_by && (
                    <p className="mt-1 text-xs font-semibold text-center" style={{ color: '#DE256D' }}>
                      Un regalo di {item.reserved_by}
                    </p>
                  )}
                  <div className="mt-2.5 flex flex-col gap-2.5">
                  {item.url && (
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="flex items-center justify-center gap-1.5 w-full rounded-full border border-neutral-200 bg-white px-4 py-2 text-[11px] md:text-xs font-semibold text-neutral-700 transition hover:border-neutral-400 hover:bg-neutral-50"
                    >
                      <ExternalLink className="w-3 h-3" />
                      {(() => {
                        try { return new URL(item.url).hostname.replace('www.', '') }
                        catch { return 'Negozio' }
                      })()}
                    </a>
                  )}
                  <div className="flex w-full items-center justify-center gap-1">
                    {confirmDeleteId === item.id ? (
                      <div className="flex w-full min-w-0 max-w-full flex-nowrap items-center gap-1.5">
                        <button
                          onClick={(e) => { e.stopPropagation(); remove(item.id) }}
                          disabled={deletingId === item.id}
                          className="inline-flex flex-1 basis-0 min-w-0 items-center justify-center gap-1 overflow-hidden whitespace-nowrap rounded-full bg-neutral-900 px-2 py-1.5 text-[11px] md:px-2.5 md:text-xs font-semibold text-white hover:bg-neutral-800 disabled:opacity-50"
                        >
                          {deletingId === item.id && (
                            <Loader2 className="h-3 w-3 shrink-0 animate-spin" />
                          )}
                          <span className="truncate">Elimina?</span>
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(null) }}
                          className="flex-1 basis-0 min-w-0 overflow-hidden truncate whitespace-nowrap rounded-full border border-border px-2 py-1.5 text-[11px] md:px-2.5 md:text-xs font-semibold text-muted-foreground hover:bg-muted/50"
                        >
                          Annulla
                        </button>
                      </div>
                    ) : (
                      <>
                        {item.is_reserved && (
                          <button
                            onClick={(e) => { e.stopPropagation(); unblockItem(item.id) }}
                            disabled={unblockingId === item.id}
                            title="Sblocca oggetto"
                            className="flex h-8 w-8 items-center justify-center rounded-full text-amber-600 transition hover:bg-amber-50 disabled:opacity-50"
                          >
                            {unblockingId === item.id ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Link2Off className="h-3.5 w-3.5" />
                            )}
                          </button>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); toggleHidden(item.id, item.is_hidden) }}
                          disabled={togglingHideId === item.id}
                          title={item.is_hidden ? 'Mostra nella wishlist pubblica' : 'Nascondi dalla wishlist pubblica'}
                          className={`flex h-8 w-8 items-center justify-center rounded-full transition disabled:opacity-50 ${
                            item.is_hidden
                              ? 'text-sky-500 hover:bg-sky-50'
                              : 'text-muted-foreground hover:bg-neutral-100 hover:text-primary'
                          }`}
                        >
                          {togglingHideId === item.id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : item.is_hidden ? (
                            <EyeOff className="h-3.5 w-3.5" />
                          ) : (
                            <Eye className="h-3.5 w-3.5" />
                          )}
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); openEdit(item) }}
                          title="Modifica"
                          className="flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition hover:bg-neutral-100 hover:text-primary"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(item.id) }}
                          title="Elimina"
                          className="flex h-8 w-8 items-center justify-center rounded-full text-red-600 transition hover:bg-muted"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </>
                    )}
                  </div>
                  </div>
                </div>
                  </motion.article>
                </SortableItemCard>
              ))}
            </motion.div>
          </SortableItemsGrid>
        )}
      </main>

      <ItemFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        userId={user.id}
        item={editing}
        wishlistId={activeBoardId}
        kind={activeBoard?.kind ?? 'wishlist'}
        onSaved={onSaved}
      />
      <CategoryManagerDialog
        open={categoryManagerOpen}
        onOpenChange={setCategoryManagerOpen}
        userId={user.id}
        categories={customCategories}
        onChange={setCustomCategories}
      />
      <WishlistBoardManager
        open={boardManagerOpen}
        onOpenChange={setBoardManagerOpen}
        userId={user.id}
        boards={boards}
        onChange={onBoardsChange}
      />
      <ItemDetailDialog
        open={detailOpen}
        onOpenChange={setDetailOpen}
        item={detailItem}
        items={filtered}
        onNavigate={setDetailItem}
        isOwner={true}
        ownerDisplayName={profile?.display_name?.trim() || profile?.username || undefined}
        ownerShippingAddress={(profile as any)?.shipping_address ?? null}
        ownerShippingRecipient={(profile as any)?.shipping_recipient ?? null}
        onItemReserved={(updatedItem) => {
          setItems(prev => prev.map(i => i.id === updatedItem.id ? { ...i, ...updatedItem } : i))
          setDetailItem(prev => prev ? { ...prev, ...updatedItem } : prev)
        }}
      />
      <DonationSettingsDialog
        open={donationOpen}
        onOpenChange={setDonationOpen}
        userId={user.id}
      />

      <footer ref={footerRef} className="mt-12 bg-neutral-900 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 flex flex-col items-center gap-4">
          <Link to="/" className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-white text-neutral-900 flex items-center justify-center">
              <Gift className="w-4 h-4" />
            </span>
            <span className="text-lg font-semibold font-logo">Make me a Gift</span>
          </Link>
          <p className="text-sm text-neutral-400">{language === 'en' ? 'Made with ♥ by' : 'Fatto con ♥ da'} <strong className="font-semibold text-neutral-300">Aura Nuccio</strong> {language === 'en' ? 'with' : 'con'} <a href="http://coderblock.ai" target="_blank" rel="noreferrer" className="underline hover:text-white transition-colors">Coderblock.ai</a></p>
          <div className="flex items-center justify-center gap-3 whitespace-nowrap text-[11px] sm:gap-6 sm:text-sm">
            <Link to="/cookie-policy" className="text-neutral-300 hover:text-white transition-colors">Cookie Policy</Link>
            <Link to="/privacy" className="text-neutral-300 hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="text-neutral-300 hover:text-white transition-colors">{language === 'en' ? 'Terms & Conditions' : 'Termini e Condizioni'}</Link>
          </div>
        </div>
      </footer>

      <button
        ref={fabRef}
        onClick={openNew}
        className={`fixed bottom-6 right-6 z-30 inline-flex items-center justify-center rounded-full w-14 h-14 transition-colors duration-500 ease-in-out shadow-lg ${fabInverted ? 'bg-white text-neutral-900 hover:bg-neutral-100' : 'text-white bg-neutral-900 hover:bg-neutral-800'}`}
        title="Aggiungi desiderio"
      >
        <Plus className="h-6 w-6" />
      </button>
    </div>
  )
}
